import { createCommands } from '@tdp/common/commandHandler';
import { nextIdlePeriod } from '@tdp/common/shared';
import { ILogger } from '@tdp/common/telemetry';

import { IAuthenticatedImageFetcher } from './abstractions';
import { authenticatedImageSlice } from './slice';


interface AuthenticatedImageStoreState { [authenticatedImageSlice.name]: ReturnType<typeof authenticatedImageSlice.getInitialState> }

export interface CreateAuthenticatedImageCommandHandlerDependencies {
  logger: ILogger;
  authenticatedImageFetcher: IAuthenticatedImageFetcher;
}
export const createAuthenticatedImageCommandHandler = ({
  logger,
  authenticatedImageFetcher,
}: CreateAuthenticatedImageCommandHandlerDependencies) =>
  createCommands({
    name: 'authenticatedImageCommands',
    handlers: {
      async *loadAuthenticatedImage(path: string, getState) {
        const currentState = getState() as AuthenticatedImageStoreState;
        const maybeImage = authenticatedImageSlice.selectors.selectAuthenticatedImage(currentState, path);
        if (maybeImage?.type === 'loaded') {
          logger.trace('Image already loaded, incrementing reference count. [%s]');
          yield authenticatedImageSlice.actions.authenticatedImageReferenced({ path });
          // We're done here
          return;
        }

        yield authenticatedImageSlice.actions.authenticatedImageLoading({ path });
        const loadedImage = await authenticatedImageFetcher.readImage(path);
        if (loadedImage.type === 'failure') {
          logger.trace('Failed to load image. [%s]', { path });
          yield authenticatedImageSlice.actions.authenticatedImageLoadFailed({ path, error: loadedImage.error });
          return;
        }
        const objectUrl = URL.createObjectURL(loadedImage.data);
        logger.trace('Creating object URL mapping. [%s] => [%s]', path, objectUrl);
        yield authenticatedImageSlice.actions.authenticatedImageLoaded({ path, dataUri: objectUrl });
      },
      async *unloadAuthenticatedImage(path: string, getState) {
        yield authenticatedImageSlice.actions.authenticatedImageUnreferenced({ path });
        // Wait for the next idle period to avoid thrashing the images & repeatedly loading the same asset 
        await nextIdlePeriod();
        const currentState = getState() as AuthenticatedImageStoreState;
        const afterAction = authenticatedImageSlice.selectors.selectAuthenticatedImage(currentState, path);
        if (afterAction?.type == 'loaded' && afterAction.refCount <= 0) {
          logger.trace('Revoking object URL. [%s]', path);
          URL.revokeObjectURL(afterAction.dataUri);
          yield authenticatedImageSlice.actions.authenticatedImageRemoved({ path });
        }
      },
    },
  });
